/**
 * @file: useConversationHandle.ts
 *
 * Holds the conversation's resolved {@link ChatConversationHandle} as plain state
 * (resolution itself happens at the entry boundary — see
 * `resolveConversationHandle`). It changes at most once per conversation, so it's
 * a raw `useState`, not a subject.
 *
 * The one mutation is materialisation: the first message to a bare peer (or
 * into a deep-linked room with no thread yet) creates the room/thread, and
 * the send result carries the now-concrete ids. `materializeHandle` swaps in
 * the baked handle and reports the ids to the HOST via the injected
 * `onConversationHandleMaterialized` callback (mobile updates route params,
 * desktop updates its selection) — this hook knows nothing about navigation
 * or the host.
 */

import { useCallback, useState } from 'react';
import { BakedConversationHandle, ChatConversationHandle } from '../handle';

export interface ConversationHandleState {
  conversationHandle: ChatConversationHandle;
  /** Swap in the baked conversationHandle once the first send materialises it. */
  materializeHandle: (next: BakedConversationHandle) => void;
}

export function useConversationHandle(
  initial: ChatConversationHandle,
  onConversationHandleMaterialized?: (handle: BakedConversationHandle) => void,
): ConversationHandleState {
  const [conversationHandle, setConversationHandle] =
    useState<ChatConversationHandle>(initial);

  const materializeHandle = useCallback(
    (next: BakedConversationHandle) => {
      setConversationHandle(next);
      onConversationHandleMaterialized?.(next);
    },
    [onConversationHandleMaterialized],
  );

  return { conversationHandle, materializeHandle };
}
